/* eslint-disable import/first */
// captureInstallPrompt() must execute before other imports to ensure
// PWA beforeinstallprompt event is captured early in page load
import { captureInstallPrompt } from "@/utils/pwa";
captureInstallPrompt();

import { createApp } from "vue";
import App from "@/App.vue";
import router from "@/router";
import "@/main.css";

import { IonicVue } from "@ionic/vue";

/* Core CSS required for Ionic components to work properly */
import "@ionic/vue/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@ionic/vue/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/vue/css/padding.css";
import "@ionic/vue/css/float-elements.css";
import "@ionic/vue/css/text-alignment.css";
import "@ionic/vue/css/text-transformation.css";
import "@ionic/vue/css/flex-utils.css";
import "@ionic/vue/css/display.css";

/* Mapbox GL styles */
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

/* Theme variables */
import "@/assets/theme/variables.css";

/* Plugins */
import { VueQueryPlugin } from "@tanstack/vue-query";
import { i18n } from "@/utils/i18n";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { LOADING_OPTIONS_KEY } from "vue-echarts";

/* Utils */
import * as verifyToken from "seedgreen-shared/query/verify-token";

/* Misc */
import mapboxgl from "mapbox-gl";
import { registerSW } from "virtual:pwa-register";
import * as Sentry from "@sentry/vue";
import posthog from "posthog-js";
import { SEEDGREEN_BASE_URL } from "@/utils/seedgreen-api";
import { POSTHOG_SUPER_PROPERTIES } from "@/composables/usePosthog";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekOfYear from "dayjs/plugin/weekOfYear";
import dateStringPlugin from "@/plugins/dayjs.plugins";

// Clean up old service worker and caches on first load of new app
import { cleanupOldPWA } from "@/cleanupOldApp";
cleanupOldPWA();

const SERVICE_WORKER_UPDATE_INTERVAL = 15 * 60 * 1000;
registerSW({
	onOfflineReady() {
		console.info("App is cached and ready for offline use");
	},
	onRegisteredSW(_, swRegistration) {
		if (swRegistration) {
			setInterval(() => {
				console.info("Checking for service worker update...");
				swRegistration.update();
			}, SERVICE_WORKER_UPDATE_INTERVAL);
		}
	},
});

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN as string;

verifyToken.configure({
	redirect: false,
});

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);
dayjs.extend(weekOfYear);
dayjs.extend(dateStringPlugin);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

posthog.init("phc_IkvZUgLAuhY9y1PjZO74VzLAuoQdzaEbiO6RmkRc5wi", {
	api_host: SEEDGREEN_BASE_URL + "/ingest",
	ui_host: "https://us.posthog.com",
	capture_pageview: false,
	loaded: (posthog) => {
		if (!location.hostname.includes("gva.ai")) {
			posthog.opt_out_capturing();
			posthog.set_config({ disable_session_recording: true });
		}
		posthog.register(POSTHOG_SUPER_PROPERTIES);
	},
});

const app = createApp(App)
	.use(IonicVue, {
		// Material Design is ugly, sorry google :/
		// also, this helps us to have a consistent look across mobile and web
		mode: "ios",
		innerHTMLTemplatesEnabled: true,
	})
	.use(router)
	.use(i18n)
	.use(pinia)
	.use(VueQueryPlugin);

// Global ECharts loading options
app.provide(LOADING_OPTIONS_KEY, {
	text: i18n.global.t("common.loading-dot"),
});

Sentry.init({
	app,
	dsn: "https://969edea0bc871be8025671a38087366a@o4508098495709184.ingest.us.sentry.io/4509075884998657",
	integrations: [
		Sentry.browserTracingIntegration({ router }),
		Sentry.replayIntegration({
			maskAllText: false,
			maskAllInputs: false,
		}),
		Sentry.browserProfilingIntegration(),
	],

	release: import.meta.env.VITE_SENTRY_RELEASE ?? "local",
	environment: import.meta.env.VITE_DEPLOYED_ENV ?? "local",

	tracePropagationTargets: ["localhost", "client.gva.local"],

	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
	profilesSampleRate: 1.0,
	beforeSend(event) {
		const deployedEnv = import.meta.env.VITE_DEPLOYED_ENV ?? "local";

		if (deployedEnv === "local") {
			console.error("Sentry event sent from local environment", event);
			return null;
		}

		return event;
	},
});

router.isReady().then(() => {
	app.mount("#app");
});
